import React, { PropsWithChildren } from 'react';

import { theme } from '@sg/garnish';

import { AppContent } from './App.content';
import {
  ForceUpdateMode,
  LoadingAnimation,
  MaintenanceMode,
  PermissionsPrimer,
  useIsForceUpdateModeEnabled,
  useIsMaintenanceModeEnabled,
} from './components';
import {
  use3rdPartyIntegrations,
  useBackgroundLocationPermissionPrimer,
  useLocationPermissionPrimer,
  usePushNotificationPermissionPrimer,
} from './hooks';

// ────────────────────────────────────────────────────────────────────────────────

/**
 * Renders the post-gate render branches: force-update mode, maintenance mode,
 * the combined push + location permission primer flow or the regular app content.
 *
 * The launch-time gate (and the in-flight restart loading state) is owned by `LaunchGate` upstream.
 */
export const App = (props: PropsWithChildren) => {
  const { children } = props;

  const pushPrimer = usePushNotificationPermissionPrimer({ variants: PERMISSIONS_PRIMER_APP_BOOT_VARIANTS }); // prettier-ignore
  const locationPrimer = useLocationPermissionPrimer({ variants: PERMISSIONS_PRIMER_APP_BOOT_VARIANTS }); // prettier-ignore
  const backgroundLocationPrimer = useBackgroundLocationPermissionPrimer({
    variants: PERMISSIONS_PRIMER_APP_BOOT_VARIANTS,
  });

  // ─── Flags ───────────────────────────────────────────────────────────

  const isMaintenanceModeEnabled = useIsMaintenanceModeEnabled();
  const isForceUpdatedModeEnabled = useIsForceUpdateModeEnabled();

  const isLoading =
    pushPrimer.isPushNotificationPermissionsLoading ||
    locationPrimer.isLocationPermissionsLoading ||
    backgroundLocationPrimer.isBackgroundLocationPermissionsLoading;
  const isPrimerActive =
    pushPrimer.isPrimerActive ||
    locationPrimer.isPrimerActive ||
    backgroundLocationPrimer.isPrimerActive;

  // ─── Effects ─────────────────────────────────────────────────────────

  use3rdPartyIntegrations();

  // ─────────────────────────────────────────────────────────────────────

  if (isForceUpdatedModeEnabled) {
    return <ForceUpdateMode />;
  }

  if (isMaintenanceModeEnabled) {
    return <MaintenanceMode />;
  }

  if (isLoading) {
    return <LoadingAnimation withNavbarOffset backgroundColor={theme.colors.APP_BACKGROUND} />;
  }

  if (isPrimerActive) {
    return (
      <PermissionsPrimer
        pushPrimer={pushPrimer}
        locationPrimer={locationPrimer}
        backgroundLocationPrimer={backgroundLocationPrimer}
      />
    );
  }

  return <AppContent>{children}</AppContent>;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PERMISSIONS_PRIMER_APP_BOOT_VARIANTS: PermissionsPrimerVariants = ['A'];

// ─── Types ───────────────────────────────────────────────────────────────────

type PermissionsPrimerVariants = Parameters<
  typeof usePushNotificationPermissionPrimer
>[0]['variants'];
